import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';

import { getPermissionData } from '../api/permissionApi';
import { EMULATION_FLAG, STORAGE_KEYS } from '../constants/storageKeys';
import { useAuth } from '../contexts/AuthContext';
import { ROUTE_PERMISSIONS } from '../constants/routePermissions';
import { logger } from '../utils/logger';
import { resolveUserId } from '../utils/userId';
import {
  BinaryFlag,
  GRANTED_FLAG_STRINGS,
  GRANTED_FLAG_VALUES,
  GrantedFlagString,
  PermissionResponseData,
  RoutePermissionEntry,
} from '../types/permissions';
import {
  VUE_CACHE_STORES,
  VueCacheStoreName,
  clearVueCache,
  readVueCache,
  writeVueCache,
} from '../utils/vueLocalCache';

const STORAGE_KEY_REAL = STORAGE_KEYS.permissionSnapshot;
const STORAGE_KEY_EMULATED = STORAGE_KEYS.permissionSnapshotEmulated;

/**
 * Emulation-aware cache slot picker. Mirrors Vue's branching in
 * `user-permission.ts:69-126` — emulation toggles which slot the
 * fetched snapshot is read from and written to.
 *
 *   isEmulate=true  → emulatedPermission IDB store + dedicated
 *                     localStorage mirror key
 *   isEmulate=false → real-user permission slots
 *
 * Keeping the slots separate means the real user's cache survives
 * across emulation sessions, so toggling emulation off doesn't trigger
 * a re-fetch.
 */
/**
 * True while an emulation session is active. Emulation is treated as a
 * live, NON-CACHED identity: compose-permission is always fetched fresh for
 * the emulated user and the result is never persisted (see `load`). This
 * matches the product requirement — an emulated session must reflect the
 * emulated user's current permissions and must not pollute either cache slot.
 */
const isEmulatingNow = (): boolean =>
  typeof window !== 'undefined'
  && window.localStorage.getItem(STORAGE_KEYS.isEmulate) === EMULATION_FLAG.ON;

const currentCacheSlot = (): {
  idb: VueCacheStoreName;
  ls: string;
} => {
  return isEmulatingNow()
    ? { idb: VUE_CACHE_STORES.emulatedPermission, ls: STORAGE_KEY_EMULATED }
    : { idb: VUE_CACHE_STORES.permission, ls: STORAGE_KEY_REAL };
};

/**
 * Hard ceiling on the boot fetch. Only fires when `status` is still
 * `'loading'` past this duration — i.e. when neither the success nor
 * error branch of `load()` has run (true network hang).
 *
 * Sized to comfortably exceed:
 *   • axios's own HTTP timeout (`HTTP_REQUEST_TIMEOUT_MS = 10s`)
 *   • one 401-refresh-and-retry round (~10s more)
 *   • a small buffer
 *
 * The previous 8s value was BELOW the actual round-trip on slow
 * networks: the v2 POST could legitimately complete at ~11s, but the
 * timer fired at 8s and force-flipped status to 'error' — which the
 * dep loader's fail-fast then read as "abort". 30s removes that race
 * while still guarding against a true infinite hang.
 */
const PERMISSION_FETCH_HARD_TIMEOUT_MS = 30000;

/**
 * The permission-snapshot load states, i.e. exactly what `usePermissions().status`
 * can be. Exported so consumers that branch on the status (e.g. the menu's
 * empty-panel message) can type it as the real union instead of a bare `string`,
 * which would let a typo'd value fall through their default branch unnoticed.
 */
export type PermissionStatus = 'idle' | 'loading' | 'ready' | 'error';

interface PermissionContextValue {
  status: PermissionStatus;
  hasPermissionForRoute: (path: string) => boolean;
  hasOperationPermission: (operationId: string) => boolean;
  /**
   * True only when the loaded permission response actually includes the
   * `operationPermissions` map. The minimized response (used by the menu)
   * sometimes strips this map; callers can use this flag to decide whether
   * to fail-open instead of treating "missing map" as "denied".
   */
  hasOperationPermissionsLoaded: () => boolean;
  /** Vue-parity domain gate — true when domainPermissions[domainId] has
   *  at least one granted flag. See the callback's doc for details. */
  hasDomainAccess: (domainId: string) => boolean;
  /**
   * Raw permission payload — exposed for derived per-domain selectors
   * (e.g. `useDomainPermissionMap`). Returns the same object every render
   * via the underlying ref, so callers must not mutate it.
   */
  getRawPermissionData: () => PermissionResponseData | null;
  /**
   * Reload the snapshot.
   *   reload()       — uses cache if present, fetches only when empty
   *                    (mirrors Vue `fetchPermissionDetails()`).
   *   reload(true)   — forces a fresh fetch regardless of cache, used
   *                    when an upstream version diff says the data is
   *                    stale (mirrors `fetchPermissionDetails(true)`).
   */
  reload: (force?: boolean) => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | undefined>(
  undefined
);

/**
 * Module-level mirror of the current load status. Updated whenever the
 * Provider's React state changes. Exposed so non-React code (dependency
 * loader factories, side-effect modules) can observe the boot state
 * without subscribing to the context.
 *
 * Read via `getPermissionStatus()`. Do NOT mutate from outside.
 */
let externalStatus: PermissionStatus = 'idle';
export const getPermissionStatus = (): PermissionStatus => externalStatus;

/**
 * Module-level mirror of the current in-memory permission snapshot. This is
 * the single source of truth for the loaded permissions and is the ONLY place
 * an emulated snapshot lives (emulation is never cached). Non-React boot code
 * (e.g. `loadPermissions`) reads it via `getPermissionSnapshot()` instead of a
 * cache slot, so it works identically for the real and emulated identities.
 *
 * Read-only from outside — kept in sync by the Provider. Do NOT mutate.
 */
let externalSnapshot: PermissionResponseData | null = null;
export const getPermissionSnapshot = (): PermissionResponseData | null =>
  externalSnapshot;

const cacheLoad = (): PermissionResponseData | null => {
  try {
    const { ls } = currentCacheSlot();
    const raw = localStorage.getItem(ls);
    return raw ? (JSON.parse(raw) as PermissionResponseData) : null;
  } catch {
    return null;
  }
};

/**
 * Persist the permission snapshot to BOTH the localStorage mirror (sync)
 * and IndexedDB (async, AWAITED).
 *
 * The previous implementation fired the IDB write as fire-and-forget
 * (`void writeVueCache(...).catch(...)`), which produced a real bug:
 * if the tab/component unmounted before the floating promise settled,
 * the IDB write was lost. The log showed
 * `MasteryData.VersionUpdateStore.userPermissions = 318` surviving from
 * a previous boot WHILE `LocalCache.permission` was empty — exact
 * symptom of an in-flight IDB write being abandoned. `await`ing the
 * write forces caller-level promise chaining; `cacheSave` now blocks
 * until both layers are durably written.
 */
const cacheSave = async (data: PermissionResponseData): Promise<void> => {
  const { idb, ls } = currentCacheSlot();
  try {
    localStorage.setItem(ls, JSON.stringify(data));
  } catch {
    /* quota exceeded — IDB stays authoritative */
  }
  try {
    await writeVueCache(idb, data);
  } catch (err) {
    logger.warn('[PermissionContext] IDB write failed', err);
  }
};

/**
 * Clear BOTH slots — used on logout when both identities should be
 * dropped. The existing `deleteAllDatabases` logout path covers IDB
 * via blanket DB deletion; this helper covers the localStorage mirrors
 * for both slots in one call.
 *
 * For emulation toggling we don't call this — the user-toggle UI in
 * `BookMark.tsx` / `EmulationModal.tsx` does a full `window.location
 * .reload()` (Vue parity), and the boot effect then loads the slot
 * matching the new identity. No in-place slot wipe is needed.
 */
const cacheClearAll = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY_REAL);
    localStorage.removeItem(STORAGE_KEY_EMULATED);
  } catch {
    /* noop */
  }
  void clearVueCache(VUE_CACHE_STORES.permission).catch(() => {
    /* noop */
  });
  void clearVueCache(VUE_CACHE_STORES.emulatedPermission).catch(() => {
    /* noop */
  });
};

/**
 * Async hydration from IndexedDB. The synchronous `cacheLoad()` reads
 * localStorage so the menu can paint on first frame; this helper runs
 * after mount to upgrade the in-memory snapshot with whatever IDB has
 * (which may be fresher when the user just logged in on another tab).
 */
const hydrateFromIdb = async (): Promise<PermissionResponseData | null> => {
  const { idb } = currentCacheSlot();
  const value = await readVueCache<PermissionResponseData>(idb);
  return value ?? null;
};

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // CRITICAL: read `loading` alongside `isAuthenticated`. During the
  // first render of a page reload, AuthProvider's state is
  // `{ user: null, token: null, loading: true }` — `initializeAuth`
  // hasn't run yet. If we treated `isAuthenticated === false` as
  // "logged out" we'd wipe the cache milliseconds before AuthContext
  // re-renders with `loading: false, isAuthenticated: true` and we'd
  // refetch every single reload. Gating on `!authLoading` distinguishes
  // "auth still hydrating" from "user genuinely logged out".
  const { isAuthenticated, loading: authLoading, user } = useAuth();

  // Hydrate from cache on first render — Vue does the same via localCacheStore.
  const dataRef = useRef<PermissionResponseData | null>(cacheLoad());
  const [status, setStatus] = useState<PermissionStatus>(
    dataRef.current ? 'ready' : 'idle'
  );

  /**
   * Track the userId the current snapshot belongs to. Used to detect
   * identity changes — emulation toggle (`isEmulate` flip) swaps which
   * userId `resolveUserId` returns, so the snapshot in memory becomes
   * stale and must be replaced with the OTHER slot's contents.
   *
   * Initial value is whatever userId `resolveUserId` returns at mount;
   * may be null when auth is still hydrating.
   */
  const lastUserIdRef = useRef<string | null>(resolveUserId(user));
  /**
   * Bumped whenever the userId-watcher detects a change. Used as a
   * dep on the boot effect so `load()` re-fires on emulation toggle.
   */
  const [identityGeneration, setIdentityGeneration] = useState(0);

  /**
   * Watch the storage layer for emulation/userId changes. `storage`
   * events fire only for cross-tab/cross-document changes (Vue ERP's
   * tab toggling emulation, or the auth-sync iframe writing the
   * handoff). Same-tab changes (this app's UserEmulation button) are
   * NOT covered — the button should dispatch a manual `storage` event
   * or just call `window.location.reload()`. The Vue ERP does the
   * reload approach.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (
        event.key !== STORAGE_KEYS.isEmulate
        && event.key !== STORAGE_KEYS.emulatedUserId
        && event.key !== STORAGE_KEYS.userDetail
        && event.key !== null /* localStorage.clear() */
      ) {
        return;
      }
      const nextUserId = resolveUserId(user);
      if (nextUserId === lastUserIdRef.current) return;
      logger.info(
        '[PermissionContext] identity changed — switching cache slot',
        { from: lastUserIdRef.current, to: nextUserId },
      );
      lastUserIdRef.current = nextUserId;
      // Drop the stale in-memory snapshot. The next render's effect
      // sees `identityGeneration` bump and re-fires `load(false)`,
      // which rehydrates from the slot matching the NEW identity (or
      // fetches if that slot is empty).
      dataRef.current = null;
      setStatus('idle');
      setIdentityGeneration((n) => n + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user]);

  // Mirror status + snapshot into the module-level slots so non-React code
  // can read them. Initial paint sets them before any effect fires.
  externalStatus = status;
  externalSnapshot = dataRef.current;
  useEffect(() => {
    externalStatus = status;
    externalSnapshot = dataRef.current;
  }, [status]);

  /**
   * Mirrors Vue `hasPermissionForRoute(path)` exactly:
   *   1. Permissions never loaded  → false
   *   2. Path not in routePermissions map → false
   *   3. routeId not in server response → false
   *   4. else → !!entry.hasPermission
   *
   * Exact match is tried first; if nothing is found, falls back to
   * pattern matching where `:param` segments in a ROUTE_PERMISSIONS key
   * act as wildcards (e.g. `/settings/api-keys/:apiKeyId` matches
   * `/settings/api-keys/some-uuid`).
   */
  const hasPermissionForRoute = useCallback(
    (path: string): boolean => {
      const data = dataRef.current;
      if (!data) return false;
      const routeMap = data.permissions?.routePermissions;
      if (!routeMap) return false;

      let routeId = ROUTE_PERMISSIONS[path];

      if (!routeId) {
        const pathParts = path.split('/');
        for (const key of Object.keys(ROUTE_PERMISSIONS)) {
          const keyParts = key.split('/');
          if (
            keyParts.length === pathParts.length &&
            keyParts.every((k, i) => k.startsWith(':') || k === pathParts[i])
          ) {
            routeId = ROUTE_PERMISSIONS[key];
            break;
          }
        }
      }

      if (!routeId) return false;
      const entry: RoutePermissionEntry | undefined = routeMap[routeId];
      return !!entry?.hasPermission;
    },
    []
  );

  /**
   * Mirrors Vue `hasDomainAccess(domainId)` (`user-permission.ts:846-859`):
   * true when the user has AT LEAST ONE granted flag under
   * `domainPermissions[domainId].permissions`. Fail-closed: no snapshot, no
   * domain entry, or no granted flag → false.
   *
   * The flag's WIRE type is not numeric. RW-Users-Service's
   * `DomainPermissionDTO` fields are `TryStateEnum`, observed on the wire as
   * the STRING `"TRUE"`/`"FALSE"` (this app's actual v2 payload), and
   * elsewhere as native boolean `true`/`false` or numeric `1`/`0`. Vue's own
   * `perms[key] == ResolvedComment.TRUE` (loose `==`, `TRUE = 1`) does NOT
   * match `"TRUE"` — `ToNumber("TRUE")` is `NaN` — so a strict port of Vue's
   * comparison silently denies every real grant. This is the same class of
   * bug already found and fixed for CloseHub's domain-CRUD check (see
   * `isGranted()` in `features/closeHub/utils/access.ts`). Accept every
   * form actually observed on the wire, same as that fix.
   */
  const hasDomainAccess = useCallback((domainId: string): boolean => {
    const data = dataRef.current;
    const perms = data?.permissions?.domainPermissions?.[domainId]?.permissions;
    if (!perms || typeof perms !== 'object') return false;
    return Object.values(perms).some((flag) => {
      if (GRANTED_FLAG_VALUES.includes(flag as boolean | BinaryFlag)) return true;
      if (typeof flag === 'string') {
        const normalised = flag.trim().toLowerCase();
        return GRANTED_FLAG_STRINGS.includes(normalised as GrantedFlagString);
      }
      return false;
    });
  }, []);

  /**
   * Mirrors Vue's read of `permissions.operationPermissions[id].hasPermission`
   * (UserEmulation.vue → onBeforeMount). Returns false until permissions land
   * so the gated UI stays hidden during the skeleton phase.
   */
  const hasOperationPermission = useCallback(
    (operationId: string): boolean => {
      const data = dataRef.current;
      if (!data) return false;
      const opMap = data.permissions?.operationPermissions;
      if (!opMap) return false;
      const entry = opMap[operationId];
      return !!entry?.hasPermission;
    },
    []
  );

  const hasOperationPermissionsLoaded = useCallback(
    (): boolean => {
      const data = dataRef.current;
      return !!data?.permissions?.operationPermissions;
    },
    []
  );

  const getRawPermissionData = useCallback(
    (): PermissionResponseData | null => dataRef.current,
    []
  );

  /**
   * Coalesces concurrent fetches. Every caller that asks while a fetch
   * is already in flight gets the same promise back — no duplicate POST,
   * no race between the boot useEffect and the dep loader's `reload()`.
   *
   * Cleared in `finally` so the next genuine refresh (post-completion
   * version bump, manual retry, etc.) is allowed to fire.
   */
  const inflightLoadRef = useRef<Promise<void> | null>(null);

  /**
   * Cache-or-fetch decision lives HERE, mirroring Vue's
   * `fetchPermissionDetails(versionUpdated?)` in
   * `RW-ERP-Service/src/stores/permissions/user-permission.ts:69-132`.
   *
   *   load(false) — default — "use cache if you have it, fetch only if empty".
   *                 The same call the page makes when the version diff says
   *                 nothing changed.
   *   load(true)  — "force-refresh, server says the data is stale".
   *                 The caller (dep loader) passes this when
   *                 `updatedNonMasterIds.includes('userPermissions')`.
   *
   * This is the single source of truth for "should the v2 POST fire".
   * The dep loader no longer makes its own cache-presence decision — it
   * just passes the version flag through.
   */
  /**
   * SINGLE entry point for "give me a permission snapshot".
   *
   * Walks every cache layer before falling through to the network:
   *
   *   1. in-memory `dataRef.current`           (sync, free)
   *   2. localStorage `rw.permission.snapshot` (sync, ~1ms)
   *   3. IndexedDB `LocalCache.permission`     (async, ~5-30ms)
   *   4. network POST /v2/users/composer/permission/{userId}
   *
   * `force=true` skips 1–3 unconditionally (Vue's `versionUpdated=true`
   * branch). `force=false` only fetches when ALL THREE caches miss.
   *
   * The previous implementation only checked layer 1, so when localStorage
   * was empty but IDB had data, every dep-loader `reload(false)` raced
   * past the synchronously-empty in-memory ref and hit the network
   * before the parallel useEffect-driven hydrate could populate it.
   * Walking all three layers in one place eliminates that race.
   */
  const load = useCallback((force: boolean = false): Promise<void> => {
    // Emulation is a live, non-cached identity: ALWAYS fetch the emulated
    // user's compose-permission fresh and never read OR write a cache slot.
    // `mustFetch` therefore short-circuits every cache layer below and the
    // success path skips persistence. This is the fix for "emulate → call
    // compose permission with the emulated user id, but don't store in cache".
    const emulating = isEmulatingNow();
    const mustFetch = force || emulating;

    if (inflightLoadRef.current) {
      logger.info('[PermissionContext] coalescing with in-flight load');
      return inflightLoadRef.current;
    }

    // Layer 1 — in-memory hit. Synchronous, no I/O. Skipped when emulating.
    if (!mustFetch && dataRef.current?.permissions) {
      logger.info('[PermissionContext] cache hit (in-memory) — skipping fetch');
      setStatus('ready');
      return Promise.resolve();
    }

    // Layer 2 — synchronous localStorage mirror. Re-read in case the
    // initial useRef snapshot ran before localStorage was populated by
    // a parallel handoff iframe / another tab.
    if (!mustFetch) {
      const fromLs = cacheLoad();
      if (fromLs?.permissions) {
        logger.info(
          '[PermissionContext] cache hit (localStorage mirror) — skipping fetch',
        );
        dataRef.current = fromLs;
        externalSnapshot = fromLs;
        setStatus('ready');
        return Promise.resolve();
      }
    }

    const run = (async () => {
      // Layer 3 — IndexedDB. The authoritative store; localStorage is
      // just a sync mirror written alongside. Skipped when emulating.
      if (!mustFetch) {
        const fromIdb = await hydrateFromIdb();
        if (fromIdb?.permissions) {
          logger.info(
            '[PermissionContext] cache hit (IndexedDB) — skipping fetch',
          );
          dataRef.current = fromIdb;
          externalSnapshot = fromIdb;
          // Repopulate the sync mirror so future first-paints hit Layer 2.
          // Use the CURRENT slot — emulation-aware so the emulated mirror
          // doesn't get hydrated from the real user's IDB and vice versa.
          try {
            const { ls } = currentCacheSlot();
            localStorage.setItem(ls, JSON.stringify(fromIdb));
          } catch {
            /* quota — IDB stays authoritative */
          }
          setStatus('ready');
          return;
        }
      }

      // Layer 4 — network.
      // resolveUserId(null) reads localStorage.userDetail directly; not
      // captured at hook-create time so reload() always sees the latest.
      // When emulating it resolves to the emulated userId (see userId.ts).
      const userId = resolveUserId(null);
      if (!userId) {
        logger.warn(
          '[PermissionContext] no userId resolvable — skipping fetch',
        );
        setStatus('error');
        return;
      }

      logger.info('[PermissionContext] fetching v2 compose-permission', {
        force,
        emulating,
        userId,
      });
      setStatus('loading');
      try {
        const response = await getPermissionData(userId, true);
        const next = response?.data;
        if (!next?.permissions) {
          logger.warn(
            '[PermissionContext] response had no permissions block — using empty fallback',
            { response },
          );
        }
        const safe: PermissionResponseData = next ?? {
          permissions: {
            routePermissions: {},
            domainPermissions: {},
            domainAttributePermissions: {},
          },
        };
        dataRef.current = safe;
        externalSnapshot = safe;
        if (emulating) {
          // Emulated permissions are intentionally NOT persisted — keep the
          // snapshot in memory only so the page boots against the emulated
          // user without polluting either cache slot.
          logger.info(
            '[PermissionContext] emulation — emulated permissions held in memory only (not cached)',
          );
        } else {
          // AWAIT — caller-level chaining ensures we don't `setStatus('ready')`
          // and resolve the promise before the cache is durably persisted.
          // Otherwise the next reload sees `LocalCache.permission` empty and
          // refetches even though the previous boot ostensibly "succeeded".
          await cacheSave(safe);
        }
        logger.info(
          '[PermissionContext] fetch success — snapshot ready',
          {
            routePermissions: Object.keys(
              safe.permissions?.routePermissions ?? {},
            ).length,
            domainPermissions: Object.keys(
              safe.permissions?.domainPermissions ?? {},
            ).length,
            domainAttributePermissions: Object.keys(
              safe.permissions?.domainAttributePermissions ?? {},
            ).length,
          },
        );
        setStatus('ready');
      } catch (err) {
        logger.error('[PermissionContext] fetch failed', err);
        setStatus('error');
      }
    })();

    inflightLoadRef.current = run.finally(() => {
      inflightLoadRef.current = null;
    });
    return inflightLoadRef.current;
  }, []);

  // Boot — a single call to load(false). The function itself walks the
  // four cache layers (in-memory → localStorage → IndexedDB → network)
  // and decides what to do. This replaces the previous useEffect-driven
  // hydrate that raced against the dep loader.
  useEffect(() => {
    // Auth still hydrating — do NOTHING. Specifically do NOT clear the
    // cache. AuthProvider's initial render has `loading: true` and a
    // null user; the next render after `initializeAuth` resolves flips
    // it to authenticated. Wiping in between would erase a perfectly
    // good cached snapshot every reload.
    if (authLoading) {
      logger.info('[PermissionContext] auth still hydrating — waiting');
      return;
    }

    if (!isAuthenticated) {
      logger.info('[PermissionContext] logged out — clearing both cache slots');
      dataRef.current = null;
      // Wipe BOTH slots on logout — the next user might be different,
      // and emulation state from a prior session must not leak.
      cacheClearAll();
      setStatus('idle');
      return;
    }
    void load(false);

    /*
      Hard ceiling: if neither the success nor error branch flips status
      within `PERMISSION_FETCH_HARD_TIMEOUT_MS`, the fetch is genuinely
      hung (axios timeout never fired, refresh-retry stuck, etc.). Force-
      clear the skeleton so the user can still navigate. Logs warn-level
      on fire so a future "stuck in loading" report has a console trail.
    */
    const safety = window.setTimeout(() => {
      setStatus((current) => {
        if (current === 'loading') {
          logger.warn(
            '[PermissionContext] safety timeout — forcing status to error',
            { ms: PERMISSION_FETCH_HARD_TIMEOUT_MS },
          );
          return 'error';
        }
        return current;
      });
    }, PERMISSION_FETCH_HARD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(safety);
    };
    // `identityGeneration` bumps on emulation toggle (storage event) so
    // the effect re-fires under the new identity. `load` reads
    // `currentCacheSlot()` at call time, picking up the new slot.
  }, [isAuthenticated, authLoading, load, identityGeneration]);

  const value = useMemo<PermissionContextValue>(
    () => ({
      status,
      hasPermissionForRoute,
      hasOperationPermission,
      hasOperationPermissionsLoaded,
      hasDomainAccess,
      getRawPermissionData,
      reload: load,
    }),
    [
      status,
      hasPermissionForRoute,
      hasOperationPermission,
      hasOperationPermissionsLoaded,
      hasDomainAccess,
      getRawPermissionData,
      load,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextValue => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used inside <PermissionProvider>');
  }
  return ctx;
};

/**
 * Non-throwing variant of `usePermissions`. Returns `null` when there is no
 * surrounding provider — used by components that integrate permissions
 * opportunistically (e.g. `<DataTable attributePermissions={…}>`) and must
 * remain renderable in standalone tests / Storybook without a provider.
 */
export const usePermissionsOptional = (): PermissionContextValue | null =>
  useContext(PermissionContext) ?? null;
